import Input from 'components/Input';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import useDebounce from 'hooks/useDebounce';
import { AddressContext } from 'pages/offer';
import { ActionButton } from 'styles/global.styles';

export interface IInputSearchAddress {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface IPlaces {
  placeId: string;
  fullAddress: string;
  mainText: string;
  secondaryText: string;
}

interface IPlacePrediction {
  placePrediction: {
    placeId: string;
    text: {
      text: string;
    };
    structuredFormat: {
      mainText: {
        text: string;
      };
      secondaryText: {
        text: string;
      };
    };
  };
}

const InputSearchAddress = ({ onClick, ...props }: IInputSearchAddress): JSX.Element => {
  const [places, setPlacesState] = useState<IPlaces[]>([]);
  const context = useContext(AddressContext);

  if (!context) {
    throw new Error('AddressContext must be used within a Provider');
  }

  const { address, setAddress, addressValidationError, setStepValidation } = context;

  const [inputValue, setInputValue] = useState<string>('');
  const [isListVisible, setIsListVisible] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>();
  const debouncedInputValue = useDebounce<string>(inputValue, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const placesRef = useRef<IPlaces[]>([]);
  const suggestionsLoaded = useRef(false);

  const setPlaces = (newPlaces: IPlaces[]) => {
    placesRef.current = newPlaces;
    setPlacesState(newPlaces);
  };

  const loadSuggestions = useCallback(async (inputValue: string) => {
    const API_KEY = process.env.REACT_APP_PLACES_NEW_API_KEY;
    const URL = 'https://places.googleapis.com/v1/places:autocomplete';

    if (!API_KEY) {
      console.error('API key is missing. Check your environment variables.');
      return;
    }

    try {
      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': API_KEY,
        },
        body: JSON.stringify({
          input: inputValue,
          includedRegionCodes: ['se'],
        }),
      });

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      const json = await response.json();

      const suggestedPlaces: IPlaces[] = json.suggestions.map((place: IPlacePrediction) => {
        const { mainText, secondaryText } = place.placePrediction.structuredFormat;

        return {
          placeId: place.placePrediction.placeId,
          fullAddress: place.placePrediction.text.text,
          mainText: mainText.text,
          secondaryText: secondaryText.text,
        };
      });

      setPlaces(suggestedPlaces);
      setIsListVisible(true);
      setError(null);
    } catch (error) {
      if (error instanceof Error) {
        setPlaces([]);
        setError(error);
        setIsListVisible(false);
      }
    }
  }, []);

  const getPlaces = useCallback(() => {
    if (debouncedInputValue) {
      const placeInList = placesRef.current.find(
        (place) => place.fullAddress === debouncedInputValue
      );

      if (!placeInList && !suggestionsLoaded.current) {
        loadSuggestions(debouncedInputValue);
        suggestionsLoaded.current = true;
      }
    } else {
      setPlaces([]);
      setIsListVisible(false);
      suggestionsLoaded.current = false;
    }
  }, [debouncedInputValue, loadSuggestions]);

  useEffect(() => {
    suggestionsLoaded.current = false;
    getPlaces();
  }, [debouncedInputValue, getPlaces]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsListVisible(false);
      }
    }

    function handleWindowBlur() {
      setIsListVisible(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [wrapperRef]);

  return (
    <>
      <Wrapper ref={wrapperRef} {...props}>
        <SearchAddressInput
          data-1p-ignore
          type="text"
          id="address"
          aria-label="address"
          placeholder="Adress"
          error={addressValidationError}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setStepValidation((prev) => ({ ...prev, address: '' }));

            if (!e.target.value.trim().length) {
              setAddress('');
            }
          }}
          onFocus={() => {
            if (inputValue && !error) {
              setIsListVisible(true);
            }
          }}
          onBlur={(e) => {
            const placeInList = places.find((place) => place.fullAddress === e.target.value);

            if (address && !placeInList) {
              setInputValue(address);
            }
          }}
        />

        {isListVisible && (
          <PlacesList>
            {places.map((item) => (
              <Place
                key={item.placeId}
                onClick={() => {
                  setAddress(item.fullAddress);
                  setInputValue(item.fullAddress);
                  setIsListVisible(false);
                }}
              >
                <div>{item.mainText}</div>
                <div>{item.secondaryText}</div>
              </Place>
            ))}
          </PlacesList>
        )}
      </Wrapper>

      <ActionButton disabled={!address} onClick={onClick}>
        Fortsätt
      </ActionButton>
    </>
  );
};

export default InputSearchAddress;

const Wrapper = styled.div`
  position: relative;
`;

const SearchAddressInput = styled(Input)`
  padding: 12px 14px;
`;

const PlacesList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: center;
  background-color: white;
  z-index: 10;
  opacity: 1;
  overflow-y: auto;
  border: 1px solid lightgray;
  pointer-events: auto;
  border-radius: 20px;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
`;

const Place = styled.div`
  text-align: start;
  font-size: 14px;
  font-weight: normal;
  line-height: 20px;
  position: relative;
  color: rgb(0, 0, 0);
  cursor: pointer;
  transition-property: color, background-color;
  transition-duration: 200ms;
  transition-timing-function: cubic-bezier(0.2, 0.8, 0.4, 1);
  padding: 0.75rem 1rem;

  &:hover {
    background-color: rgb(243, 243, 243);
  }
`;
